import { SubdivisionOverrideTypeEnum } from '@vatos-pas/common';

import { toNumber } from './currency';

// The two Cost Structure percentage fields the resolver can read. Region and
// Subdivision both expose each field; the caller picks which one applies
// (Piecework for piecework payouts, Special Jobs for special-job payouts).
export type CostStructurePercentageField =
  | 'pieceworkPercentage'
  | 'specialJobsPercentage';

// Minimal shape the resolver needs from an already-fetched Region. RegionDto
// satisfies this (its percentages are non-null numbers).
export type CostStructureRegion = {
  pieceworkPercentage: number | null;
  specialJobsPercentage: number | null;
};

// Minimal shape the resolver needs from an already-fetched Subdivision.
export type CostStructureSubdivision = {
  overrideRegionCostPerBoard?: boolean | null;
  overrideType?: SubdivisionOverrideTypeEnum | null;
  pieceworkPercentage: number | null;
  specialJobsPercentage: number | null;
};

export type ResolveCostStructurePercentageInput = {
  region: CostStructureRegion;
  subdivision: CostStructureSubdivision;
  field: CostStructurePercentageField;
};

// Mirrors the Region/Subdivision resolution in
// repair-payment-calculations.service; a divergence would prefill an amount
// the backend then recomputes differently. Returns undefined when the
// governing value is absent (Fixed with no Subdivision value).
export const resolveCostStructurePercentage = ({
  region,
  subdivision,
  field,
}: ResolveCostStructurePercentageInput): number | undefined => {
  const regionValue = region[field];
  const subValue = subdivision[field];

  if (!subdivision.overrideRegionCostPerBoard) {
    return regionValue ?? undefined;
  }

  if (subdivision.overrideType === SubdivisionOverrideTypeEnum.OFFSET) {
    return (Number(subValue) || 0) + (Number(regionValue) || 0);
  }

  // Override ON + Fixed: the Subdivision value replaces the Region value.
  return subValue ? Number(subValue) : undefined;
};

// Minimal shape the Allow Payout Amount Editing resolver needs from an
// already-fetched Region. The field is optional so records fetched before the
// column existed (or partial projections) fail safe to OFF.
export type PayoutEditingRegion = {
  allowPayoutAmountEditing?: boolean | null;
};

// Minimal shape the resolver needs from an already-fetched Subdivision.
export type PayoutEditingSubdivision = {
  overrideRegionCostPerBoard?: boolean | null;
  allowPayoutAmountEditing?: boolean | null;
};

export type ResolveAllowPayoutAmountEditingInput = {
  region?: PayoutEditingRegion | null;
  subdivision?: PayoutEditingSubdivision | null;
};

// Mirrors repair-payment.service exactly; a divergence from the backend rule
// would cause spurious 400s or wrongly locked fields. Unlike the percentage
// resolution, Production Cost Mode (Fixed/Offset) plays no part. Any missing
// record or undefined field fails safe to false — editing is never granted by
// accident.
export const resolveAllowPayoutAmountEditing = ({
  region,
  subdivision,
}: ResolveAllowPayoutAmountEditingInput): boolean => {
  if (subdivision?.overrideRegionCostPerBoard) {
    return subdivision.allowPayoutAmountEditing === true;
  }

  return region?.allowPayoutAmountEditing === true;
};

// Rounds half-up to two decimals — the currency precision every payout value
// is persisted with (the API's numeric(10,2) columns).
export const round2 = (value: number): number => Math.round(value * 100) / 100;

// Returns an empty string when the PO Total or percentage is missing/zero,
// matching how the Calculated Cost field renders before a value is available.
export const calculateCostStructurePayout = (
  poTotal: number | string | null | undefined,
  percentage: number | null | undefined,
): string => {
  if (!poTotal || !percentage) return '';

  const po = toNumber(poTotal);
  if (po === null) return '';

  return round2((po * percentage) / 100).toFixed(2);
};
